import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getItemsPaged, deleteItem } from "./PlannerService";
import { exportPlanner } from "../ExportService";
import "./PlannerList.css";

export default function PlannerList() {
  const [items, setItems] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [projectFilter, setProjectFilter] = useState("ALL");
  const [groupByProject, setGroupByProject] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const loadMoreRef = useRef(null);
  const nav = useNavigate();

  const loadPage = async (nextPage, reset = false) => {
    if (loading) return;
    setLoading(true);
    const data = await getItemsPaged({
      page: nextPage,
      size: 25,
      priority: priorityFilter,
      dateFilter,
      category: categoryFilter,
      project: projectFilter,
      search: searchText
    });
    const nextItems = Array.isArray(data.items) ? data.items : [];
    setItems((prev) => (reset ? nextItems : [...prev, ...nextItems]));
    setPage(nextPage);
    setHasMore(Boolean(data.hasMore));
    setLoading(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}" permanently?\nThis cannot be undone.`))
      return;

    await deleteItem(id);
    loadPage(0, true);
  };

  /* ---------- DATE HELPERS ---------- */

  const today = new Date();
  today.setHours(0,0,0,0);

  const parseDate = (d) => d ? new Date(d + "T00:00:00") : null;

  const getDate = (i) =>
    i.dueDate || i.eventDate || i.paymentDate;

  const isCompleted = (i) =>
    i.status === "DONE" || i.status === "SKIPPED";

  const isOverdue = (i) => {
    const d = parseDate(getDate(i));
    return d && d < today && !isCompleted(i);
  };

  const isToday = (i) => {
    const d = parseDate(getDate(i));
    return d && d.getTime() === today.getTime() && !isCompleted(i);
  };

  const isUpcoming = (i) => {
    const d = parseDate(getDate(i));
    return d && d > today && !isCompleted(i);
  };

  const isNoDate = (i) =>
    !getDate(i) && !isCompleted(i);

  useEffect(() => {
    loadPage(0, true);
  }, [priorityFilter, dateFilter, categoryFilter, projectFilter, searchText]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMore && !loading) {
            loadPage(page + 1);
          }
        });
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [page, hasMore, loading, priorityFilter, dateFilter, categoryFilter, projectFilter, searchText]);

  /* ---------- SEARCH HIGHLIGHT ---------- */

  const highlightMatch = (text) => {
    if (!searchText) return text;
    const parts = text.split(new RegExp(`(${searchText})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchText.toLowerCase()
        ? <mark key={i} className="planner-list-highlight">{part}</mark>
        : part
    );
  };

  const clearSearch = () => {
    setSearchText("");
    searchRef.current.focus();
  };

  /* ---------- ROW ---------- */

  const renderRow = (i) => (
    <tr key={i.id} className={`planner-list-row ${isOverdue(i) ? "planner-list-row-overdue" : ""}`}>
      <td className="planner-list-title-cell">{highlightMatch(i.title)}</td>
      <td>{i.type}</td>
      <td>{getDate(i) || "-"}</td>
      <td><PriorityBadge priority={i.priority} /></td>
      <td>{i.status}</td>
      <td>{i.category || "-"}</td>
      <td>{i.projectName || "-"}</td>
      <td>
        <button onClick={() => nav(`edit/${i.id}`)}>✏️</button>
        <button onClick={() => handleDelete(i.id, i.title)}>🗑️</button>
      </td>
    </tr>
  );

  return (
    <div className="planner-list-container">
      <h2>Planner</h2>

      <button onClick={() => nav("new")} className="planner-list-add-btn">
        ➕ Add
      </button>
      <button onClick={() => nav("upcoming")} className="planner-list-add-btn planner-list-next-day-btn">
        🗓️ Upcoming
      </button>
      <button onClick={() => nav("next-day")} className="planner-list-add-btn planner-list-next-day-btn">
        Next Day Planner
      </button>
      <button onClick={() => nav("payments")} className="planner-list-add-btn planner-list-next-day-btn">
        Payments Dashboard
      </button>
      <button onClick={() => nav("/calendar")} className="planner-list-add-btn planner-list-next-day-btn">
        Calendar
      </button>
      <button onClick={() => exportPlanner().catch(console.error)} className="planner-list-add-btn planner-list-next-day-btn">
        ⬇ Export CSV
      </button>

      {/* 🔥 TOOLBAR */}
      <div className="planner-list-toolbar">

        {/* SEARCH */}
        <div className="planner-list-search-wrapper">
          <input
            ref={searchRef}
            type="text"
            placeholder="Search title..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="planner-list-search-input"
          />
          {searchText && (
            <button onClick={clearSearch} className="planner-list-clear-btn">✖</button>
          )}
        </div>

        {/* PRIORITY FILTER */}
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="planner-list-select"
        >
          <option value="ALL">All Priorities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
          <option value="SOMEDAY">Someday</option>
        </select>

        {/* DATE FILTER */}
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="planner-list-select"
        >
          <option value="ALL">All Dates</option>
          <option value="OVERDUE">Overdue</option>
          <option value="TODAY">Today</option>
          <option value="UPCOMING">Upcoming</option>
          <option value="NO_DATE">No Date</option>
          <option value="COMPLETED">Completed</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="planner-list-select"
        >
          <option value="ALL">All Categories</option>
          <option value="WORK">Work</option>
          <option value="PERSONAL">Personal</option>
          <option value="FINANCE">Finance</option>
          <option value="HEALTH">Health</option>
          <option value="FAMILY">Family</option>
          <option value="EDUCATION">Education</option>
          <option value="BUSINESS">Business</option>
          <option value="SHOPPING">Shopping</option>
          <option value="TRAVEL">Travel</option>
          <option value="BILLING">Billing</option>
          <option value="INVESTMENT">Investment</option>
          <option value="SUBSCRIPTION">Subscription</option>
          <option value="INSURANCE">Insurance</option>
          <option value="HOME">Home</option>
          <option value="SOCIAL">Social</option>
          <option value="GOALS">Goals</option>
          <option value="PROJECT">Project</option>
          <option value="OTHER">Others</option>
        </select>

        <input
          type="text"
          placeholder="Project..."
          value={projectFilter === "ALL" ? "" : projectFilter === "NONE" ? "(none)" : projectFilter}
          onChange={(e) => {
            const v = e.target.value;
            if (!v.trim()) setProjectFilter("ALL");
            else if (v.trim() === "(none)") setProjectFilter("NONE");
            else setProjectFilter(v);
          }}
          className="planner-list-search-input"
          style={{ maxWidth: 220 }}
        />

        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={groupByProject}
            onChange={(e) => setGroupByProject(e.target.checked)}
          />
          Group by project
        </label>

      </div>

      {groupByProject ? (
        Object.entries(
          items.reduce((acc, it) => {
            const key = it.projectName?.trim() || "(No project)";
            acc[key] = acc[key] || [];
            acc[key].push(it);
            return acc;
          }, {})
        )
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([project, projectItems]) => (
            <div key={project} style={{ marginTop: 16 }}>
              <h3 style={{ margin: "6px 0 10px" }}>{project}</h3>
              <table className="planner-list-table">
                <thead>
                  <tr className="planner-list-header-row">
                    <th>Title</th>
                    <th>Type</th>
                    <th>Due</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>Project</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{projectItems.map(renderRow)}</tbody>
              </table>
            </div>
          ))
      ) : (
        <table className="planner-list-table">
          <thead>
            <tr className="planner-list-header-row">
              <th>Title</th>
              <th>Type</th>
              <th>Due</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Category</th>
              <th>Project</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(renderRow)}
          </tbody>
        </table>
      )}
      <div ref={loadMoreRef} className="planner-list-load-more">
        {loading ? "Loading..." : hasMore ? "Scroll to load more" : "End of list"}
      </div>
    </div>
  );
}

/* ---------- PRIORITY BADGE ---------- */

function PriorityBadge({ priority }) {
  const priorityClass = (priority || "").toLowerCase();

  return (
    <span className={`planner-list-priority-badge planner-list-priority-${priorityClass}`}>
      {priority}
    </span>
  );
}
